import React, { useEffect, useState } from 'react';
import { useTheme } from '../../styles/ThemeProvider';
import { FilterBar, DateRangePicker, KpiCard } from '../../components/analytics';
import { formatTRY, formatPct } from '../../utils/analytics/format';
import { useSearchParams } from 'react-router-dom';
import { rangeFromUrl } from '../../utils/analytics/dateRange';
import { useFirmalar } from '../../hooks/useFirmalar';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const KATEGORI_LABELS = {
  BANKA_KART: 'Banka Kart',
  YEMEK_KARTI: 'Yemek Kartı',
  ONLINE_PLATFORM: 'Online Platform',
  NAKIT: 'Nakit',
};

export default function KomisyonDetayPage() {
  const { theme } = useTheme();
  // Cockpit drill-down bağlamı (dönem + firma)
  const [searchParams] = useSearchParams();
  const [range, setRange] = useState(() => rangeFromUrl(searchParams));
  const [firma, setFirma] = useState(() => searchParams.get('firma') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { data: firmalar } = useFirmalar();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ baslangic: range.start, bitis: range.end });
        if (firma) params.append('firma', firma);
        const res = await fetch(`${API}/api/reports/komisyon-detay?${params}`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [range.start, range.end, firma]);

  return (
    <div style={{ padding: theme.space.lg, display: 'flex', flexDirection: 'column', gap: theme.space.lg, background: theme.color.surfaceAlt, minHeight: '100vh' }}>
      <header>
        <h1 style={{ margin: 0, color: theme.color.text, fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold }}>
          Komisyon Detayı
        </h1>
        <div style={{ color: theme.color.textSoft, fontSize: theme.font.size.sm, marginTop: 4 }}>
          POS ödeme yöntemlerinin komisyon yükü kanal bazında.
        </div>
      </header>

      <FilterBar>
        <DateRangePicker value={range} onChange={setRange} />
        <select
          value={firma}
          onChange={(e) => setFirma(e.target.value)}
          style={{
            padding: `${theme.space.xs}px ${theme.space.md}px`,
            borderRadius: theme.radius.sm,
            border: `1px solid ${theme.color.border}`,
            background: theme.color.surface,
            color: theme.color.text,
            fontSize: theme.font.size.xs,
            fontFamily: theme.font.family,
          }}
        >
          <option value="">Tüm Firmalar</option>
          {(firmalar || []).map((f) => <option key={f.kod} value={f.kod}>{f.ad}</option>)}
        </select>
      </FilterBar>

      {loading && <div style={{ padding: theme.space.md, color: theme.color.textSoft }}>Yükleniyor...</div>}
      {error && <div style={{ padding: theme.space.md, color: theme.color.danger, background: theme.color.dangerBg, borderRadius: theme.radius.sm }}>Hata: {error}</div>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: theme.space.md }}>
            <KpiCard label="Toplam Komisyon" value={formatTRY(data.TOPLAM, { decimals: 2 })} />
            <KpiCard label="Toplam Ciro" value={formatTRY(data.TOPLAM_CIRO, { decimals: 0 })} />
            <KpiCard label="Komisyon Yükü %" value={formatPct(data.TOPLAM_PCT)} />
          </div>

          <section style={{ background: theme.color.surface, padding: theme.space.lg, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}` }}>
            <h3 style={{ marginTop: 0, color: theme.color.text }}>Kategori Bazlı</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.color.border}`, textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kategori</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Komisyon (TL)</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Ciroya %</th>
                </tr>
              </thead>
              <tbody>
                {['BANKA_KART', 'YEMEK_KARTI', 'ONLINE_PLATFORM', 'NAKIT'].map((kat) => {
                  const tutar = data[kat] || 0;
                  const ciroPct = data.TOPLAM_CIRO > 0 ? (tutar / data.TOPLAM_CIRO * 100) : 0;
                  return (
                    <tr key={kat} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                      <td style={{ padding: 8 }}>{KATEGORI_LABELS[kat]}</td>
                      <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{formatTRY(tutar, { decimals: 2 })}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{formatPct(ciroPct)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          <section style={{ background: theme.color.surface, padding: theme.space.lg, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}` }}>
            <h3 style={{ marginTop: 0, color: theme.color.text }}>Kanal Detayı</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.color.border}`, textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kanal</th>
                  <th style={{ padding: 8 }}>Kategori</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Tutar (TL)</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Oran</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Komisyon</th>
                </tr>
              </thead>
              <tbody>
                {(data.kanal_detay || []).map((row) => (
                  <tr key={row.kanal_kod} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                    <td style={{ padding: 8 }}>{row.kanal_ad}</td>
                    <td style={{ padding: 8, fontSize: 12, color: theme.color.textSoft }}>{KATEGORI_LABELS[row.kategori] || row.kategori}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{formatTRY(row.tutar, { decimals: 2 })}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{formatPct(row.oran_pct)}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{formatTRY(row.komisyon, { decimals: 2 })}</td>
                  </tr>
                ))}
                {(!data.kanal_detay || data.kanal_detay.length === 0) && (
                  <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: theme.color.textSoft }}>Bu dönemde kanal detayı yok</td></tr>
                )}
              </tbody>
            </table>
          </section>

          <div style={{ fontSize: 12, color: theme.color.textSoft, padding: theme.space.md }}>
            Komisyon oranlarını yönetmek için: <strong>Yönetim → Master Veriler → Komisyon Oranları</strong>
          </div>
        </>
      )}
    </div>
  );
}
